import {
  type FilterRequest,
  Order,
  type SortingRequest,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/listing_pb';
import {
  Operator,
  type PropertyConstraint,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/query_pb';
import type { PlainMessage } from '@bufbuild/protobuf';
import { Check, CollapseAllVisibility, IBreadcrumbItem, IGroup, IStyle, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesDanger,
  Checkbox,
  type ClassNamesFromIStyles,
  getGroupHeight,
  IListCellStyles,
  ListRow,
  ListRowType,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useQueryMyWorkspaces } from '../../../authz/authz.hooks';
import { useUsers } from '../../../authz/providers/UsersProvider';
import { FailedToLoadView } from '../../../components/FailedToLoadView/FailedToLoadView';
import { HeaderPure } from '../../../components/Header/Header';
import { NoItemView } from '../../../components/NoItemView/NoItemView';
import WidgetList from '../../../components/WidgetList/WidgetList';
import {
  getFieldProperty,
  getPropertyConstraint,
  ListRegisteredModelsSupportedFilterOrderKeys,
} from '../../../mlops/mlops.storage.functions';
import {
  RegisteredModelVersionWithExperiment,
  useMutationBatchDeleteRegisteredModelsAndModelVersions,
  useMutationDeleteExperiments,
  useMutationDeleteModelVersion,
  useMutationDeleteRegisteredModelAndExperiments,
  useQueriesRegisteredModelsVersionsWithExperiments,
  useQueryRegisteredModels,
} from '../../../mlops/mlops.storage.hooks';
import { useInfiniteQueryFetchAllPages } from '../../../services/hooks';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '../../../services/HTTPResponse';
import { classNameButtonReset } from '../../../themes/common';
import { CollapsedContext, SelectedContext, useSelected } from '../../../utils/contexts';
import { useDebouncedCallback } from '../../../utils/hooks';
import { SortProps } from '../../../utils/sortUtils';
import { formatError } from '../../../utils/utils';
import { LoaderView } from '../../Orchestrator/WorkflowDetail';
import { ContextMenuIconButton } from '../../Orchestrator/Workflows';
import { useConfirmDialog } from '../ConfirmDialogProvider';
import { ROUTES, WORKSPACE_DEPLOYMENT_ID_SEPARATOR } from '../constants';
import { RegisteredModelItem, RegisteredModelVersionItem, useColumns, useGroupsAndItems } from '../Models';
import { ModelTagProvider } from '../ModelTagProvider';
import { NavigationWrapperNoMagic } from '../NavigationWrapperNoMagic';
import PageWrapper from '../PageWrapper';
import { MLOpsUserActionType } from '../userActions';
import { ModelsFilter } from './ModelsFilter';
import { ModelsOrderBy, ModelsOrderByKeys } from './ModelsOrderBy';

type SelectionPanelProps = {
  className: string;
  groups: IGroup[];
  items: RegisteredModelVersionItem[];
  onBatchDelete: (p: { modelIds: string[]; versionsIds: string[] }) => void;
  selectedModels: SelectedContext;
  selectedVersions: SelectedContext;
  selectedVersionsViaModels: SelectedContext;
};

const SelectionPanel = (props: SelectionPanelProps) => {
  const { selectedModels, selectedVersions, selectedVersionsViaModels, groups, items } = props;
  const modelsCount = groups.length;
  const selectedModelsCount = !selectedModels.defaultSelected
    ? selectedModels.deviatesFromDefault.size
    : modelsCount - selectedModels.deviatesFromDefault.size;
  const { selectedVersionsViaModelsCount, totalSelectedVersionsCount, totalSelectedVersionIds } = React.useMemo<{
    selectedVersionsViaModelsCount: number;
    totalSelectedVersionsCount: number;
    totalSelectedVersionIds: Set<string>;
  }>(() => {
    // (false, false)
    if (!selectedVersions.defaultSelected && !selectedVersionsViaModels.defaultSelected) {
      // @ts-expect-error new Set methods not available in TypeScript < 5.5
      const totalSelectedVersionIds = selectedVersions.deviatesFromDefault.union(
        selectedVersionsViaModels.deviatesFromDefault
      );
      const selectedVersionsViaModelsCount = selectedVersionsViaModels.deviatesFromDefault.size;
      return {
        totalSelectedVersionsCount: totalSelectedVersionIds.size,
        totalSelectedVersionIds,
        selectedVersionsViaModelsCount,
      };
    }
    // (true, true)
    if (selectedVersions.defaultSelected && selectedVersionsViaModels.defaultSelected) {
      const allVersionIds = new Set<string>(items.map((v) => v.id));
      // @ts-expect-error new Set methods not available in TypeScript < 5.5
      const unselectedVersionIds = selectedVersions.deviatesFromDefault.union(
        selectedVersionsViaModels.deviatesFromDefault
      );
      // @ts-expect-error new Set methods not available in TypeScript < 5.5
      const totalSelectedVersionIds = allVersionIds.difference(unselectedVersionIds);
      const selectedVersionsViaModelsCount = allVersionIds.size - selectedVersionsViaModels.deviatesFromDefault.size;
      return {
        totalSelectedVersionsCount: totalSelectedVersionIds.size,
        totalSelectedVersionIds,
        selectedVersionsViaModelsCount,
      };
    }
    // (false, true)
    if (!selectedVersions.defaultSelected && selectedVersionsViaModels.defaultSelected) {
      const allVersionIds = new Set<string>(items.map((v) => v.id));
      const selectedDirectly = selectedVersions.deviatesFromDefault;
      // @ts-expect-error new Set methods not available in TypeScript < 5.5
      const selectedViaModels = allVersionIds.difference(selectedVersionsViaModels.deviatesFromDefault);
      // @ts-expect-error new Set methods not available in TypeScript < 5.5
      const totalSelectedVersionIds = selectedDirectly.union(selectedViaModels);
      return {
        totalSelectedVersionsCount: totalSelectedVersionIds.size,
        totalSelectedVersionIds,
        selectedVersionsViaModelsCount: selectedViaModels.size,
      };
    }
    // (true, false)
    if (selectedVersions.defaultSelected && !selectedVersionsViaModels.defaultSelected) {
      const allVersionIds = new Set<string>(items.map((v) => v.id));
      // @ts-expect-error new Set methods not available in TypeScript < 5.5
      const selectedDirectly = allVersionIds.difference(selectedVersions.deviatesFromDefault);
      const selectedViaModels = selectedVersionsViaModels.deviatesFromDefault;
      // @ts-expect-error new Set methods not available in TypeScript < 5.5
      const totalSelectedVersionIds = selectedViaModels.union(selectedDirectly);
      return {
        totalSelectedVersionsCount: totalSelectedVersionIds.size,
        totalSelectedVersionIds,
        selectedVersionsViaModelsCount: selectedViaModels.size,
      };
    }
    throw new Error(`Unhandled selected versions state`);
  }, [
    selectedVersions.defaultSelected,
    selectedVersions.deviatesFromDefault,
    selectedVersionsViaModels.defaultSelected,
    selectedVersionsViaModels.deviatesFromDefault,
    items,
  ]);
  const selectedVersionsDirectly = totalSelectedVersionsCount - selectedVersionsViaModelsCount;
  const anySelected = selectedModelsCount > 0 || totalSelectedVersionsCount > 0;
  const allSelected = selectedModels.defaultSelected && selectedModels.deviatesFromDefault.size === 0;

  return (
    <div className={props.className}>
      <Checkbox
        checked={allSelected}
        label="Select all"
        boxSide="end"
        onChange={() => {
          if (allSelected) {
            selectedModels.emptySelection();
            selectedVersions.emptySelection();
            selectedVersionsViaModels.emptySelection();
          } else {
            selectedModels.selectAll();
            selectedVersions.emptySelection();
            selectedVersionsViaModels.selectAll();
          }
        }}
      />
      {anySelected && (
        <>
          <p>
            Selected{' '}
            {selectedModelsCount > 0 && (
              <span>
                {selectedModelsCount} model{selectedModelsCount > 1 ? `s` : ``}
                {selectedVersionsViaModelsCount > 0 && (
                  <span>
                    {' '}
                    (with {selectedVersionsViaModelsCount} version{selectedVersionsViaModelsCount > 1 ? `s` : ``})
                  </span>
                )}
              </span>
            )}
            {selectedModelsCount > 0 && selectedVersionsDirectly > 0 && <> and </>}
            {selectedVersionsDirectly > 0 && (
              <span>
                {selectedVersionsDirectly} model version{selectedVersionsDirectly > 1 ? `s` : ``}
              </span>
            )}
          </p>
          <Button
            styles={buttonStylesDanger}
            onClick={() => {
              const modelIds: string[] = Array.from(
                !selectedModels.defaultSelected
                  ? selectedModels.deviatesFromDefault
                  : // @ts-expect-error new Set methods not available in TypeScript < 5.5
                    new Set(groups.map((g) => g.key)).difference(selectedModels.deviatesFromDefault)
              );
              const versionsIds = Array.from(totalSelectedVersionIds);
              props.onBatchDelete({ modelIds, versionsIds });
            }}
          >
            Delete Selected
          </Button>
        </>
      )}
    </div>
  );
};

type ModelsFilterState =
  | undefined
  | {
      creator?: string;
      search?: string;
      workspace?: string;
    };

const adminModelsStyles: Record<string, IStyle> = {
  buttonCheck: [
    classNameButtonReset,
    {
      alignItems: `center`,
      blockSize: `2rem`,
      display: `flex`,
      inlineSize: `2.6rem`,
      justifyContent: `center`,
      '&:disabled': {
        opacity: 0.5,
        cursor: `not-allowed`,
      },
    },
  ],
  buttonSortBy: {
    marginInlineStart: `auto`,
  },
  selectionPanel: {
    alignItems: `center`,
    display: `flex`,
    flexWrap: `wrap`,
    gap: `1rem`,
    minBlockSize: `2rem`,

    '& > p': {
      margin: 0,
    },
  },
};

type AdminModelsStyles = typeof adminModelsStyles;

export type AdminModelsPureProps = {
  collapsed: CollapsedContext;
  errorMessage?: React.ReactNode; // if set, displays full-page error message
  filter?: ModelsFilterState;
  groups: IGroup[];
  isLoadingMore?: boolean;
  items: RegisteredModelVersionItem[];
  loading?: boolean;
  onBatchDelete: (p: { modelIds: string[]; versionsIds: string[] }) => void;
  onDeleteModel: (modelId: string, projectId: string) => void;
  onDeleteModelVersion: (modelVersion: RegisteredModelVersionItem) => void;
  onGoToDeploymentDetail: (workspaceId: string, deploymentId: string) => void;
  onGoToHome: () => void;
  onGoToModelDetail: (item: RegisteredModelItem) => void;
  onGoToModelDetailInWorkspace: (model: RegisteredModelItem) => void;
  onLoadMore?: () => void;
  registeredModelVersionsWithExperimentsByRegisteredModelId: Record<string, RegisteredModelVersionWithExperiment[]>;
  setFilter: (newStateOrUpdater: ModelsFilterState | ((prev: ModelsFilterState) => ModelsFilterState)) => void;
  setSort: (props: SortProps) => void;
  sort: SortProps;
};

export const AdminModelsPure = (props: AdminModelsPureProps) => {
  const {
    collapsed,
    items,
    groups,
    onDeleteModel,
    onDeleteModelVersion,
    onGoToModelDetail,
    onGoToModelDetailInWorkspace,
    registeredModelVersionsWithExperimentsByRegisteredModelId,
    setSort,
    sort,
    filter,
    setFilter,
  } = props;
  const breadcrumbs: IBreadcrumbItem[] = [
    {
      key: `home`,
      text: `home`,
      onClick: props.onGoToHome,
    },
    {
      key: `admin-models`,
      text: `Admin Models`,
      isCurrentItem: true,
    },
  ];

  const classNames = useClassNames<AdminModelsStyles, ClassNamesFromIStyles<AdminModelsStyles>>(
    `AdminModels`,
    adminModelsStyles
  );

  const { headerColumns, itemColumns } = useColumns({
    hideModelIcon: true,
    showWorkspaceColumn: true,
    onGoToDeploymentDetail: props.onGoToDeploymentDetail,
    onGoToModelDetail: props.onGoToModelDetail,
    onRenderHeaderButtons: ({ id, projectId }: RegisteredModelItem) => {
      return (
        <ContextMenuIconButton
          items={[
            {
              key: 'view',
              text: 'View',
              onClick: () => onGoToModelDetail({ id, projectId } as RegisteredModelItem),
              iconProps: { iconName: 'Info', style: { color: 'var(--h2o-gray900)' } },
            },
            {
              key: `view-in-workspace`,
              text: 'View in workspace',
              onClick: () => onGoToModelDetailInWorkspace({ id, projectId } as RegisteredModelItem),
              iconProps: { iconName: 'OpenInNewWindow', style: { color: 'var(--h2o-gray900)' } },
            },
            {
              key: 'delete',
              text: 'Delete model',
              onClick: () => {
                onDeleteModel(id, projectId);
              },
              style: { color: 'var(--h2o-red400)' },
              iconProps: {
                iconName: 'Delete',
                style: { color: 'var(--h2o-red400)' },
              },
            },
          ]}
        />
      );
    },
    onRenderItemButtons: (modelVersion) => {
      return (
        <ContextMenuIconButton
          items={[
            {
              key: 'delete',
              text: 'Delete version',
              onClick: () => {
                onDeleteModelVersion(modelVersion);
              },
              style: { color: 'var(--h2o-red400)' },
              iconProps: {
                iconName: 'DeleteRows',
                style: { color: 'var(--h2o-red400)' },
              },
            },
          ]}
        />
      );
    },
  });
  const selectedModels = useSelected();
  const selectedVersions = useSelected();
  const selectedVersionsViaModels = useSelected();
  const selectionListCellStyles: Partial<IListCellStyles> = {
    root: {
      display: `flex`,
      justifyContent: `center`,
    },
  };
  const selectionColumnWidth = 60;
  headerColumns.splice(1, 0, {
    key: `selection`,
    name: ``,
    minWidth: selectionColumnWidth,
    maxWidth: selectionColumnWidth,
    data: {
      listCellProps: {
        emptyMessage: '',
        onRenderText: ({ id }: RegisteredModelItem) => {
          const isSelected = selectedModels.isSelected(id);
          return (
            <button
              className={classNames.buttonCheck}
              onClick={(event) => {
                event.stopPropagation();
                const newSelected = !isSelected;
                selectedModels.setSelected(id, newSelected);
                const versionsId = registeredModelVersionsWithExperimentsByRegisteredModelId[id]?.map((v) => v.id);
                if (versionsId?.length) {
                  selectedVersionsViaModels.setSelectedItems(versionsId || [], newSelected);
                }
              }}
              type="button"
            >
              <Check checked={isSelected} />
            </button>
          );
        },
        styles: selectionListCellStyles,
      },
    },
  });
  itemColumns.splice(1, 0, {
    key: `selection`,
    name: ``,
    minWidth: selectionColumnWidth,
    maxWidth: selectionColumnWidth,
    data: {
      listCellProps: {
        emptyMessage: '',
        onRenderText: ({ id }: RegisteredModelVersionItem) => {
          const selectedViaModel = selectedVersionsViaModels.isSelected(id);
          return (
            <button
              className={classNames.buttonCheck}
              disabled={selectedViaModel}
              onClick={(event) => {
                event.stopPropagation();
                selectedVersions.toggleSelected(id);
              }}
              title={selectedViaModel ? `Selected via model` : undefined}
              type="button"
            >
              <Check checked={selectedViaModel || selectedVersions.isSelected(id)} />
            </button>
          );
        },
        styles: selectionListCellStyles,
      },
    },
  });

  const handleEmptySelection = () => {
    selectedModels.emptySelection();
    selectedVersions.emptySelection();
    selectedVersionsViaModels.emptySelection();
  };
  const setFilterAndEmptySelection = (
    newStateOrUpdater: ModelsFilterState | ((prev: ModelsFilterState) => ModelsFilterState)
  ): void => {
    setFilter(newStateOrUpdater);
    handleEmptySelection();
  };
  const [searchText, setSearchText] = React.useState<string>(filter?.search || ``);
  const debouncedHandleSearch = useDebouncedCallback((newSearch) => {
    setFilterAndEmptySelection((prev) => ({
      ...prev,
      search: newSearch.trim(),
    }));
  }, 500);

  const setSortAndEmptySelection = (newSortProps: SortProps): void => {
    setSort(newSortProps);
    handleEmptySelection();
  };

  return (
    <PageWrapper>
      <HeaderPure items={breadcrumbs} pageTitle="Admin Models" />
      {props.errorMessage ? (
        <FailedToLoadView title="Failed to load models" description={props.errorMessage} />
      ) : props.loading ? (
        <LoaderView loaderText="Loading all MLOps Models..." />
      ) : (
        <WidgetList
          columns={itemColumns}
          customActions={
            <ModelsOrderBy
              className={classNames.buttonSortBy}
              descending={sort.descending}
              onChange={(key, descending) => {
                setSortAndEmptySelection({ key, descending });
              }}
              orderKey={sort.key as ModelsOrderByKeys}
            />
          }
          forceShowActionBar={Boolean(filter)}
          groupProps={{
            getGroupHeight: (group: IGroup) => getGroupHeight(group, ListRowType.GroupHeader),
            groupProps: {
              collapseAllVisibility: CollapseAllVisibility.hidden,
              onRenderHeader: (groupHeaderProps) => {
                if (!groupHeaderProps?.group) return null;
                const { group } = groupHeaderProps;
                const item = group.data.data;
                return (
                  <ListRow
                    data={item}
                    columns={headerColumns}
                    contents={item?.contents}
                    hasLeftOpenClose={true}
                    collapsed={group.isCollapsed}
                    hasOpenClose={group.count > 0}
                    onOpen={() => collapsed.toggleCollapsed(group.key)}
                    styles={(theme) => ({
                      row: {
                        '&:hover': {
                          background: theme.semanticColors?.contentBackground,
                          '.h2o-ListCell-text': {
                            color: theme.semanticColors?.textSecondary,
                          },
                        },
                      },
                    })}
                  />
                );
              },
              showEmptyGroups: true,
            },
            groups,
          }}
          isLoadingMore={props.isLoadingMore}
          items={items}
          loading={false}
          NoItemsContent={
            filter ? (
              <NoItemView
                title="No models matching active filters"
                description="Try different filters or clear all to see all models."
              />
            ) : (
              <NoItemView title="Models" description="Your cloud environment has no models." />
            )
          }
          onLoadMore={props.onLoadMore}
          searchProps={{
            placeholder: 'Search by name and description',
            onSearchChange: (newSearch: string) => {
              setSearchText(newSearch);
              debouncedHandleSearch(newSearch);
              if (!newSearch) debouncedHandleSearch.flush();
            },

            searchText: searchText,
          }}
          topPanelSlot={
            <>
              <ModelsFilter
                className={classNames.fieldsetFilter}
                onCreatorChange={(newCreator) => {
                  setFilterAndEmptySelection((prev) => ({
                    ...prev,
                    creator: newCreator,
                  }));
                }}
                onResetFilter={
                  filter &&
                  (() => {
                    setFilterAndEmptySelection(undefined);
                  })
                }
                onWorkspaceChange={(newWorkspace) => {
                  setFilterAndEmptySelection((prev) => ({
                    ...prev,
                    workspace: newWorkspace,
                  }));
                }}
                selectedCreator={filter?.creator}
                selectedWorkspace={filter?.workspace}
              />
              {Boolean(items?.length) && (
                <SelectionPanel
                  className={classNames.selectionPanel}
                  groups={groups}
                  items={items}
                  onBatchDelete={props.onBatchDelete}
                  selectedModels={selectedModels}
                  selectedVersions={selectedVersions}
                  selectedVersionsViaModels={selectedVersionsViaModels}
                />
              )}
            </>
          }
          styles={{
            searchBar: [classNames.searchBar],
            widgetContainer: [classNames.widgetContainer],
          }}
        />
      )}
    </PageWrapper>
  );
};

const useFilterAndOrderBy = (): {
  filter: ModelsFilterState;
  setFilter: (newStateOrUpdater: ModelsFilterState | ((prev: ModelsFilterState) => ModelsFilterState)) => void;
  setSort: (newStateOrUpdater: SortProps | ((prev: SortProps) => SortProps)) => void;
  sort: SortProps;
} => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchParamsRef = React.useRef(searchParams);
  searchParamsRef.current = searchParams;
  const filter = React.useMemo<ModelsFilterState>(() => {
    const fromSearchParams: ModelsFilterState = {
      creator: searchParams.get(`creator`) || undefined,
      search: searchParams.get(`search`) || undefined,
      workspace: searchParams.get(`workspace`) || undefined,
    };
    if (Object.values(fromSearchParams).some(Boolean)) {
      return fromSearchParams;
    }
    return undefined;
  }, [searchParams]);
  // Use ref to pass fresh filter to the setFilter function, which can then be used in memoized instances
  const filterRef = React.useRef(filter);
  filterRef.current = filter;
  const setFilter = (newStateOrUpdater: ModelsFilterState | ((prev: ModelsFilterState) => ModelsFilterState)) => {
    const newFilter =
      typeof newStateOrUpdater === `function` ? newStateOrUpdater(filterRef.current) : newStateOrUpdater;

    const sp = searchParamsRef.current;
    if (newFilter?.creator) {
      sp.set(`creator`, newFilter.creator);
    } else {
      sp.delete(`creator`);
    }
    if (newFilter?.search) {
      sp.set(`search`, newFilter.search);
    } else {
      sp.delete(`search`);
    }
    if (newFilter?.workspace) {
      sp.set(`workspace`, newFilter.workspace);
    } else {
      sp.delete(`workspace`);
    }
    const newSearch = sp.toString();
    if (newSearch !== window.location.search.slice(1)) {
      history.replace({ ...location, search: newSearch });
    }
  };

  const sort: SortProps = React.useMemo(() => {
    const orderKey = searchParams.get(`orderBy`);
    const descending = searchParams.get(`desc`) === `1`;
    return {
      key: (orderKey as ListRegisteredModelsSupportedFilterOrderKeys) || undefined,
      descending: descending,
    };
  }, [searchParams]);
  const sortRef = React.useRef(sort);
  sortRef.current = sort;
  const setSort = (newStateOrUpdater: SortProps | ((prev: SortProps) => SortProps)) => {
    const newOrderBy = typeof newStateOrUpdater === `function` ? newStateOrUpdater(sortRef.current) : newStateOrUpdater;
    const sp = searchParamsRef.current;
    if (newOrderBy.key) {
      sp.set(`orderBy`, newOrderBy.key);
    } else {
      sp.delete(`orderBy`);
    }
    if (newOrderBy.descending) {
      sp.set(`desc`, `1`);
    } else {
      sp.delete(`desc`);
    }
    const newSearch = sp.toString();
    if (newSearch !== window.location.search.slice(1)) {
      history.replace({ ...location, search: newSearch });
    }
  };
  return { filter, sort, setFilter, setSort };
};

const useStorageApiFilter = (filter: ModelsFilterState): undefined | PlainMessage<FilterRequest> => {
  return React.useMemo<undefined | PlainMessage<FilterRequest>>(() => {
    if (!filter) return undefined;
    const propertyConstraint: PlainMessage<PropertyConstraint>[] = [];
    if (filter.creator) {
      propertyConstraint.push(
        getPropertyConstraint<ListRegisteredModelsSupportedFilterOrderKeys>(`owner_id`, Operator.EQUAL_TO, {
          case: `idValue`,
          value: filter.creator,
        })
      );
    }
    if (filter.search) {
      propertyConstraint.push(
        getPropertyConstraint<ListRegisteredModelsSupportedFilterOrderKeys>(`name`, Operator.ICONTAINS, {
          case: `stringValue`,
          value: filter.search,
        })
      );
    }
    return { query: { clause: [{ propertyConstraint, tagConstraint: undefined as any }] } };
  }, [filter]);
};

const useStorageApiSorting = (sort: SortProps): undefined | PlainMessage<SortingRequest> => {
  return React.useMemo<undefined | PlainMessage<SortingRequest>>(() => {
    if (!sort.key) return undefined;
    return {
      property: [{ property: getFieldProperty(sort.key), order: sort.descending ? Order.DESCENDING : Order.ASCENDING }],
    };
  }, [sort]);
};

const AdminModelsConnected = () => {
  const history = useHistory();
  const { filter, setFilter, setSort, sort } = useFilterAndOrderBy();
  const storageApiFilter = useStorageApiFilter(filter);
  const storageApiSorting = useStorageApiSorting(sort);
  const registeredModelsQuery = useQueryRegisteredModels(
    {
      projectId: filter?.workspace || ``,
      filter: storageApiFilter,
      sorting: storageApiSorting,
    },
    {
      meta: {
        toastError: 'Failed to load models',
      },
      retry(failureCount, error) {
        const insufficientPermissions =
          isGrpcGatewayErrorResponse(error) && error.code === GrpcStatusCode.PERMISSION_DENIED;
        // Skip retrying if the user does not have permission to view all models, otherwise retry up to 3 times
        return !insufficientPermissions && failureCount <= 3;
      },
      select: (data) => {
        const flattenedRegisteredModels = data.pages.flatMap((page) => {
          if (!page.registeredModels) return [];
          return page.registeredModels;
        });
        return { pages: flattenedRegisteredModels, pageParams: data.pageParams };
      },
    }
  );
  const allModels = registeredModelsQuery.data?.pages;
  const { loading: versionsWithExperimentsLoading, registeredModelVersionsWithExperimentsByRegisteredModelId } =
    useQueriesRegisteredModelsVersionsWithExperiments(allModels || [], {
      select: (data) => {
        // Sort the model versions by createdTime ASC (oldest first).
        data.modelVersions.sort((a, b) =>
          Number(BigInt(a.createdTime?.seconds || 0) - BigInt(b.createdTime?.seconds || 0))
        );
        return data;
      },
    });
  const workspaceQuery = useQueryMyWorkspaces(
    {},
    {
      select: (data) => {
        const flattenedWorkspaces = data.pages.flatMap((page) => page.workspaces || []);
        return { pages: flattenedWorkspaces, pageParams: data.pageParams };
      },
    }
  );
  useInfiniteQueryFetchAllPages(workspaceQuery);
  const workspaces = workspaceQuery.data?.pages;
  const { usersAndGroupsNameMap } = useUsers();
  const { collapsed, groups, items } = useGroupsAndItems({
    showEmptyGroup: () => true,
    registeredModels: allModels,
    registeredModelVersionsWithExperimentsByRegisteredModelId,
    usersAndGroupsNameMap,
    workspaces,
  });

  const { addToast } = useToast();
  const { showDialog } = useConfirmDialog();
  const deleteModelVersionMutation = useMutationDeleteModelVersion({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete model version: ${formatError(err)}`,
      });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Model version deleted successfully.',
      });
    },
  });
  const deleteExperimentsMutation = useMutationDeleteExperiments({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete experiment: ${formatError(err)}`,
      });
    },
  });
  const handleDeleteRegisteredModelVersionWithExperiments = async (
    versionWithExperiment: RegisteredModelVersionItem
  ) => {
    await deleteModelVersionMutation.mutateAsync({
      projectId: versionWithExperiment.workspaceId || '',
      versionId: versionWithExperiment.id,
    });
    const experiment = versionWithExperiment.experiment;
    if (experiment) {
      await deleteExperimentsMutation.mutateAsync({
        experimentRequest: [{ id: experiment.id, projectId: versionWithExperiment.workspaceId || '' }],
      });
    }
  };
  const deleteModelAndExperimentsMutation = useMutationDeleteRegisteredModelAndExperiments({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete model: ${formatError(err)}`,
      });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Model deleted successfully.',
      });
    },
  });
  const batchDeleteModelAndVersionsMutation = useMutationBatchDeleteRegisteredModelsAndModelVersions({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete selected models and versions: ${formatError(err)}`,
        timeout: 20_000,
      });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Selected models and versions deleted successfully.',
      });
    },
  });

  return (
    <ModelTagProvider>
      <AdminModelsPure
        collapsed={collapsed}
        errorMessage={registeredModelsQuery.error && !filter ? formatError(registeredModelsQuery.error) : undefined} // ignore errors on filtered views, show only "nothing found" in that case
        filter={filter}
        groups={groups}
        isLoadingMore={registeredModelsQuery.isFetchingNextPage}
        items={items}
        loading={registeredModelsQuery.isLoading || versionsWithExperimentsLoading}
        onBatchDelete={({ modelIds, versionsIds }) => {
          const versionsSelectedViaModels = new Set(
            modelIds.flatMap((id) =>
              registeredModelVersionsWithExperimentsByRegisteredModelId[id].map((version) => version.id)
            )
          );
          // @ts-expect-error new Set methods not available in TypeScript < 5.5
          const versionsSelectedDirectly = new Set(versionsIds).difference(versionsSelectedViaModels);
          const deletedModelsSentence = !modelIds.length
            ? ``
            : `${modelIds.length} model${modelIds.length > 1 ? 's' : ''} (with ${
                versionsSelectedViaModels.size
              } version${versionsSelectedViaModels.size > 1 ? 's' : ''})`;
          const deleteVersionsSentence = !versionsSelectedDirectly.size
            ? ``
            : `${versionsSelectedDirectly.size} version${versionsSelectedDirectly.size > 1 ? 's' : ''}`;
          const description = `This will delete ${[deletedModelsSentence, deleteVersionsSentence]
            .filter(Boolean)
            .join(` and `)}. This action cannot be undone.`;
          showDialog?.({
            title: 'Batch delete models and versions?',
            description,
            onConfirm: () => {
              const modelsToDelete = allModels!.filter((m) => modelIds.includes(m.id));
              batchDeleteModelAndVersionsMutation.mutate({
                models: modelsToDelete,
                versionIds: Array.from(versionsSelectedDirectly),
              });
            },
          });
        }}
        onDeleteModel={(modelId, projectId) => {
          showDialog?.({
            title: 'Delete model?',
            description: 'This will delete the model and all its versions. This action cannot be undone.',
            onConfirm: () => {
              deleteModelAndExperimentsMutation.mutate({
                projectId: projectId || '',
                registeredModelId: modelId,
              });
            },
          });
        }}
        onDeleteModelVersion={(modelVersion) => {
          showDialog?.({
            title: 'Delete model version?',
            description: `This will delete version ${modelVersion.version} of the model. This action cannot be undone.`,
            onConfirm: () => handleDeleteRegisteredModelVersionWithExperiments(modelVersion),
          });
        }}
        onGoToDeploymentDetail={(workspaceId, deploymentId) => {
          window.open(`/mlops/workspaces/${workspaceId}${ROUTES.DEPLOYMENTS}/${deploymentId}`, '_blank');
        }}
        onGoToHome={() => history.push('/')}
        onGoToModelDetail={(model) => {
          history.push(`/mlops/admin/models/${model.projectId}${WORKSPACE_DEPLOYMENT_ID_SEPARATOR}${model.id}`);
        }}
        onGoToModelDetailInWorkspace={(model) => {
          window.open(`/mlops/workspaces/${model.projectId}${ROUTES.MODELS}/${model.id}`, '_blank');
        }}
        onLoadMore={registeredModelsQuery.hasNextPage ? registeredModelsQuery.fetchNextPage : undefined}
        registeredModelVersionsWithExperimentsByRegisteredModelId={
          registeredModelVersionsWithExperimentsByRegisteredModelId
        }
        setFilter={setFilter}
        setSort={setSort}
        sort={sort}
      />
    </ModelTagProvider>
  );
};

export const AdminModels = () => {
  return (
    <NavigationWrapperNoMagic requiredMLOpsGlobalPermissions={[MLOpsUserActionType.MLOPS_MODEL_REGISTRY_LIST_ALL]}>
      <AdminModelsConnected />
    </NavigationWrapperNoMagic>
  );
};
